import React, { useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { Plus, Copy, Trash2, GripVertical, ChevronDown } from 'lucide-react';
import TextBlock from './blocks/TextBlock';
import ImageBlock from './blocks/ImageBlock';
import VideoBlock from './blocks/VideoBlock';
import SeparatorBlock from './blocks/SeparatorBlock';
import PlayerComparisonBlock from './blocks/PlayerComparisonBlock';
import MaxPrepsItemBlock from './blocks/MaxPrepsItemBlock';
import WhatsNextItemBlock from './blocks/WhatsNextItemBlock';
import LeaderboardItemBlock from './blocks/LeaderboardItemBlock';
import Block247SportsItemBlock from './blocks/Block247SportsItemBlock';
import XFactorItemBlock from './blocks/XFactorItemBlock';
import HotTakesItemBlock from './blocks/HotTakesItemBlock';
import PowerRankingItemBlock from './blocks/PowerRankingItemBlock';
import MomentumMeterItemBlock from './blocks/MomentumMeterItemBlock';
import TriviaItemBlock from './blocks/TriviaItemBlock';
import PickemItemBlock from './blocks/PickemItemBlock';
import TeamComparisonItemBlock from './blocks/TeamComparisonItemBlock';
import TransactionItemBlock from './blocks/TransactionItemBlock';
import ArticlePreview from './ArticlePreview';
import '../../styles/ArticleBuilder.css';

const ArticleBuilder = ({ initialBlocks = [], onChange, onSave, isSaving = false, title = '', authorName = '', headerImage = '' }) => {
  const [blocks, setBlocks] = useState(initialBlocks.length > 0 ? initialBlocks : []);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [expandedDropdown, setExpandedDropdown] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { distance: 8 }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const addBlock = useCallback((type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      ...getDefaultBlockContent(type),
    };
    setBlocks([...blocks, newBlock]);
    onChange([...blocks, newBlock]);
  }, [blocks, onChange]);

  const updateBlock = useCallback((blockId, content) => {
    console.log('[ArticleBuilder] updateBlock called with blockId:', blockId, 'content:', content);
    const updatedBlocks = blocks.map(block =>
      block.id === blockId ? { ...block, ...content } : block
    );
    console.log('[ArticleBuilder] Updated blocks:', updatedBlocks);
    setBlocks(updatedBlocks);
    onChange(updatedBlocks);
  }, [blocks, onChange]);

  const deleteBlock = useCallback((blockId) => {
    const updatedBlocks = blocks.filter(block => block.id !== blockId);
    setBlocks(updatedBlocks);
    setSelectedBlockId(null);
    onChange(updatedBlocks);
  }, [blocks, onChange]);

  const duplicateBlock = useCallback((blockId) => {
    const blockToDuplicate = blocks.find(b => b.id === blockId);
    if (blockToDuplicate) {
      const newBlock = {
        ...blockToDuplicate,
        id: `block-${Date.now()}`,
      };
      const index = blocks.findIndex(b => b.id === blockId);
      const updatedBlocks = [
        ...blocks.slice(0, index + 1),
        newBlock,
        ...blocks.slice(index + 1),
      ];
      setBlocks(updatedBlocks);
      onChange(updatedBlocks);
    }
  }, [blocks, onChange]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex(b => b.id === active.id);
      const newIndex = blocks.findIndex(b => b.id === over.id);
      const reorderedBlocks = arrayMove(blocks, oldIndex, newIndex);
      setBlocks(reorderedBlocks);
      onChange(reorderedBlocks);
    }
  };

  return (
    <div className="article-builder">
      <div className="builder-layout">
        <div className="builder-sidebar-left">
          <div className="sidebar-section">
            <h4>Add Blocks</h4>
            <div className="block-dropdowns">
              <div className="dropdown-group">
                <button
                  className="dropdown-header"
                  onClick={() => setExpandedDropdown(expandedDropdown === 'basic' ? null : 'basic')}
                >
                  <ChevronDown size={18} style={{ transform: expandedDropdown === 'basic' ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                  <span>Basic</span>
                </button>
                {expandedDropdown === 'basic' && (
                  <div className="dropdown-items">
                    <button onClick={() => { addBlock('text'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Text</button>
                    <button onClick={() => { addBlock('image'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Image</button>
                    <button onClick={() => { addBlock('video'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Video</button>
                    <button onClick={() => { addBlock('separator'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Separator</button>
                    <button onClick={() => { addBlock('player-comparison'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Player Comparison</button>
                  </div>
                )}
              </div>

              <div className="dropdown-group">
                <button
                  className="dropdown-header"
                  onClick={() => setExpandedDropdown(expandedDropdown === 'sports' ? null : 'sports')}
                >
                  <ChevronDown size={18} style={{ transform: expandedDropdown === 'sports' ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                  <span>Sports Stats</span>
                </button>
                {expandedDropdown === 'sports' && (
                  <div className="dropdown-items">
                    <button onClick={() => { addBlock('maxpreps'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> MaxPreps</button>
                    <button onClick={() => { addBlock('whatsnext'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> What's Next</button>
                    <button onClick={() => { addBlock('leaderboard'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Leaderboard</button>
                    <button onClick={() => { addBlock('247sports'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> 247Sports</button>
                  </div>
                )}
              </div>

              <div className="dropdown-group">
                <button
                  className="dropdown-header"
                  onClick={() => setExpandedDropdown(expandedDropdown === 'analysis' ? null : 'analysis')}
                >
                  <ChevronDown size={18} style={{ transform: expandedDropdown === 'analysis' ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                  <span>Analysis</span>
                </button>
                {expandedDropdown === 'analysis' && (
                  <div className="dropdown-items">
                    <button onClick={() => { addBlock('xfactor'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> X-Factor</button>
                    <button onClick={() => { addBlock('hottakes'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Hot Takes</button>
                    <button onClick={() => { addBlock('powerranking'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Power Ranking</button>
                    <button onClick={() => { addBlock('momentummeter'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Momentum Meter</button>
                  </div>
                )}
              </div>

              <div className="dropdown-group">
                <button
                  className="dropdown-header"
                  onClick={() => setExpandedDropdown(expandedDropdown === 'interactive' ? null : 'interactive')}
                >
                  <ChevronDown size={18} style={{ transform: expandedDropdown === 'interactive' ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                  <span>Interactive</span>
                </button>
                {expandedDropdown === 'interactive' && (
                  <div className="dropdown-items">
                    <button onClick={() => { addBlock('trivia'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Trivia</button>
                    <button onClick={() => { addBlock('pickems'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Pickems</button>
                    <button onClick={() => { addBlock('teamcomparison'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Team Comparison</button>
                    <button onClick={() => { addBlock('transactions'); setExpandedDropdown(null); }} className="dropdown-item"><Plus size={16} /> Transactions</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {selectedBlockId && (
            <div className="sidebar-section">
              <h4>Block Info</h4>
              <p className="block-id">ID: {selectedBlockId}</p>
            </div>
          )}
        </div>

        <div className="builder-canvas">
          <div className="canvas-header">
            <h3>Article Content Builder</h3>
          </div>

          {blocks.length === 0 ? (
            <div className="empty-canvas">
              <p>No blocks yet. Add your first block to get started!</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={blocks.map(b => b.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="blocks-container">
                  {blocks.map((block) => (
                    <BlockWrapper
                      key={block.id}
                      block={block}
                      isSelected={selectedBlockId === block.id}
                      onSelect={setSelectedBlockId}
                      onUpdate={updateBlock}
                      onDelete={deleteBlock}
                      onDuplicate={duplicateBlock}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <ArticlePreview title={title} blocks={blocks} authorName={authorName} headerImage={headerImage} />
      </div>
    </div>
  );
};

const BlockWrapper = ({ block, isSelected, onSelect, onUpdate, onDelete, onDuplicate }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const renderBlock = () => {
    switch (block.type) {
      case 'text':
        return <TextBlock block={block} onUpdate={onUpdate} />;
      case 'image':
        return <ImageBlock block={block} onUpdate={onUpdate} />;
      case 'video':
        return <VideoBlock block={block} onUpdate={onUpdate} />;
      case 'player-comparison':
        return <PlayerComparisonBlock block={block} onUpdate={onUpdate} />;
      case 'separator':
        return <SeparatorBlock block={block} />;
      case 'maxpreps':
        return <MaxPrepsItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'whatsnext':
        return <WhatsNextItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'leaderboard':
        return <LeaderboardItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case '247sports':
        return <Block247SportsItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'xfactor':
        return <XFactorItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'hottakes':
        return <HotTakesItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'powerranking':
        return <PowerRankingItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'momentummeter':
        return <MomentumMeterItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'trivia':
        return <TriviaItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'pickems':
        return <PickemItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'teamcomparison':
        return <TeamComparisonItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      case 'transactions':
        return <TransactionItemBlock block={block} onUpdate={onUpdate} onDelete={onDelete} />;
      default:
        return null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`block-wrapper ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
      onClick={() => onSelect(block.id)}
    >
      <div className="block-header" {...attributes} {...listeners}>
        <GripVertical size={18} className="drag-handle" />
        <span className="block-type">{block.type}</span>
      </div>

      <div className="block-content">
        {renderBlock()}
      </div>

      <div className="block-actions">
        <button
          onClick={() => onDuplicate(block.id)}
          className="action-btn duplicate"
          title="Duplicate block"
        >
          <Copy size={16} />
        </button>
        <button
          onClick={() => onDelete(block.id)}
          className="action-btn delete"
          title="Delete block"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const getDefaultBlockContent = (type) => {
  switch (type) {
    case 'text':
      return {
        content: '',
        segments: [],
        formatting: {
          level: 'p',
          fontFamily: 'sans-serif',
          listType: 'none'
        }
      };
    case 'image':
      return { src: '', caption: '', alt: '' };
    case 'video':
      return { url: '', title: '', provider: 'youtube' };
    case 'separator':
      return {};
    case 'player-comparison':
      return { 
        player1_name: '', 
        player2_name: '',
        player1_team: '',
        player2_team: '',
        player1_stats: {},
        player2_stats: {}
      };
    case 'maxpreps':
      return {
        gameId: '',
        finalScore: '',
        teamStats: {},
        topPerformers: '',
        scoringDrives: []
      };
    case 'whatsnext':
      return {
        nextGame: '',
        record: '',
        opponent: '',
        gameDateTime: ''
      };
    case 'leaderboard':
      return {
        leaders: {
          passing: '',
          sacks: '',
          interceptions: '',
          rushingTouchdowns: ''
        }
      };
    case '247sports':
      return {
        headshot: '',
        playerName: '',
        bio: '',
        seasonStats: {},
        quote: ''
      };
    case 'xfactor':
      return {
        xfactorType: 'player',
        subjectName: '',
        description: '',
        importance: '',
        keyStat: ''
      };
    case 'hottakes':
      return {
        upsetAlert: '',
        boldPrediction: '',
        underTheRadar: ''
      };
    case 'powerranking':
      return {
        rankings: []
      };
    case 'momentummeter':
      return {
        teamA: '',
        teamB: '',
        momentum: 50,
        explanation: ''
      };
    case 'trivia':
      return {
        question: '',
        choices: [],
        correctAnswerIndex: 0,
        explanation: ''
      };
    case 'pickems':
      return {
        title: '',
        description: '',
        picks: []
      };
    case 'teamcomparison':
      return {
        team1_name: '',
        team2_name: '',
        week: '',
        team1_stats: {},
        team2_stats: {}
      };
    case 'transactions':
      return {
        title: '',
        transactions: []
      };
    default:
      return {};
  }
};

export default ArticleBuilder;
